/**
 * VaultDriveCopyBadge — "Copied from Google Drive", with the original a click away.
 *
 * A COPY badge, deliberately not the composer's live-Drive chip: a file
 * brought into the Library from Drive is stationary — nothing links back,
 * nothing syncs, nothing in Drive ever changes. The badge says where the
 * bytes came from; the link opens the original for whoever wants to compare.
 * It renders only for a placement the server marked as a cloud copy.
 */

import React from 'react';
import { Tooltip } from 'antd';
import { useLanguage } from '@/hooks/useLanguage';
import { safeHttpUrl } from '@/utils/safeHttpUrl';
import { ExternalLinkIcon, GoogleDriveGlyph } from '@modules/conversations/components/drive/driveIcons';
import type { VaultFile } from '../types';

interface Props {
  file: Pick<VaultFile, 'source_type' | 'cloud_provider' | 'source'>;
  /** The viewer's title has room for the words; the table cell shows the glyph alone. */
  showLabel?: boolean;
}

export const isDriveCopy = (file: Pick<VaultFile, 'source_type' | 'cloud_provider'>): boolean =>
  file.source_type === 'cloud_file' && file.cloud_provider === 'google_drive';

export const VaultDriveCopyBadge: React.FC<Props> = ({ file, showLabel = false }) => {
  const { t } = useLanguage();
  if (!isDriveCopy(file)) return null;
  const original = safeHttpUrl(file.source?.web_view_link);
  const label = t('vault.driveCopy.badge');
  const hint = original ? t('vault.driveCopy.openOriginal') : t('vault.driveCopy.hint');

  const body = (
    <>
      <GoogleDriveGlyph size={12} />
      {showLabel && <span className="vault-drive-copy__label">{label}</span>}
      {original && <ExternalLinkIcon size={11} />}
    </>
  );

  return (
    <Tooltip title={`${label} — ${hint}`} mouseEnterDelay={0.4}>
      {original ? (
        <a
          className="vault-drive-copy"
          href={original}
          target="_blank"
          rel="noopener noreferrer"
          onClick={(e) => e.stopPropagation()}
          aria-label={`${label}. ${hint}`}
        >
          {body}
        </a>
      ) : (
        <span className="vault-drive-copy" aria-label={label}>{body}</span>
      )}
    </Tooltip>
  );
};

export default VaultDriveCopyBadge;
